import type { FC } from 'react';
import { removeCachedRequests, useFetch } from 'loti-request';
import { useTranslation } from 'react-i18next';

import Modal from 'app/components/Modal';
import { getControleBackendUrl, getPandapiHeaders } from 'app/utils/api';
import Button from 'app/components/Button';
import CircleLoader from 'app/components/Loader/CircleLoader';
import BlacklistPlayerItems from 'app/pages/BlacklistPage/BlacklistPlayerItems';

import style from './style.scss';
import { getBlacklistPlayerItemAsHistoryItem } from './utils';

interface Props {
  player?: BlacklistPlayer;
  playerItems?: BlacklistPlayer[];
  onClose: () => void;
}

const PlayerHistoryModal: FC<Props> = ({ player, playerItems, onClose }) => {
  const [t] = useTranslation();

  removeCachedRequests('/histories');

  const { data: history, status } = useFetch<BlacklistPlayerHistoryItem[]>({
    url: getControleBackendUrl(`/blacklist/players/${player?.id}/histories`),
    headers: getPandapiHeaders(),
    preventRequest: !player
  });

  const displayedItems = playerItems ? getBlacklistPlayerItemAsHistoryItem(playerItems) : history;

  return (
    <Modal displayed onClickOutside={onClose}>
      <div className={style.head}>
        {player ? t('blacklist-history-title') : t('blacklist-info-title')}
      </div>
      {player && (status === 'NOT_SEND' || status === 'LOADING') && (
        <div className={style.loaderBlock}>
          <CircleLoader className={style.loader} size={30} color="#3399F4" />
          <div className={style.loaderTitle}>{t('blacklist-history-loading-title')}</div>
          <div className={style.loaderDescription}>
            {t('blacklist-history-loading-description')}
          </div>
        </div>
      )}
      {(!player || status === 'SUCCESS') && displayedItems !== undefined && (
        <BlacklistPlayerItems
          playerItems={displayedItems}
          type={!player ? 'player_list' : 'history'}
          className={style.itemsContent}
        />
      )}
      {(status === 'FAILED' || (status === 'SUCCESS' && history === undefined)) && (
        <div className={style.errorBlock}>
          <div className={style.errorTitle}>{t('blacklist-history-error-title')}</div>
          <div className={style.errorDescription}>{t('blacklist-history-error-description')}</div>
        </div>
      )}
      <div className={style.footer}>
        <Button onClick={onClose} variation="empty">
          {t('common-close')}
        </Button>
      </div>
    </Modal>
  );
};

PlayerHistoryModal.displayName = 'PlayerHistoryModal';

export default PlayerHistoryModal;
